import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import { ClassLogEditModal, EditLoggedClassLink, EditRequestChip } from '../components/ClassLogEdit';
import TaskRows, { emptyTask, tasksToSend } from '../components/TaskRows';
import { useTracker } from '../context/TrackerContext';
import {
  getMentorStudents,
  getMentors,
  getRecentClasses,
  submitClass,
  updateClassStatus,
} from '../lib/api';
import type { MentorInfo, RecentClassRow, StudentBasic, TaskItem } from '../lib/api';

// ==================== HELPERS ================================

const todayStr = () => new Date().toISOString().slice(0, 10);

const COMPLETION_OPTIONS = ['Task Given', 'Completed', 'Not Completed'] as const;
type CompletionStatus = typeof COMPLETION_OPTIONS[number];
type AbsentType = 'Informed' | 'NotInformed' | 'Leave';

const EMPTY_FORM = {
  studentIds:       [] as string[],
  date:             todayStr(),
  attendance:       'Present' as 'Present' | 'Absent',
  absentType:       'NotInformed' as AbsentType,
  portion:          '',
  topic:            '',
  startTime:        '',
  endTime:          '',
  completionStatus: 'Task Given' as CompletionStatus,
  remark:           '',
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{label}</label>
    {children}
  </div>
);

// ==================== LOG CLASS TAB ==========================

const LogClassTab: React.FC<{
  coordinatorMentorId: string;
  allMentors: MentorInfo[];
}> = ({ coordinatorMentorId, allMentors }) => {
  const [form, setForm]               = useState({ ...EMPTY_FORM });
  // The tasks given in this class: each ticked student gets each one
  const [tasks, setTasks]             = useState<TaskItem[]>([emptyTask()]);
  const [loading, setLoading]         = useState(false);
  const [progress, setProgress]       = useState('');
  const [selectedMentorId, setSelectedMentorId] = useState('');
  const [students, setStudents]       = useState<StudentBasic[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');

  const handleMentorChange = async (mentorId: string) => {
    setSelectedMentorId(mentorId);
    setForm(prev => ({ ...prev, studentIds: [] }));
    setStudentSearch('');
    if (!mentorId) { setStudents([]); return; }
    setLoadingStudents(true);
    try {
      const res = await getMentorStudents(mentorId);
      setStudents(res.students);
    } catch (err: any) {
      toast.error('Could not load students: ' + err.message);
    } finally {
      setLoadingStudents(false);
    }
  };

  const filteredStudents = studentSearch
    ? students.filter(s =>
        s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
        s.studentId.toLowerCase().includes(studentSearch.toLowerCase()))
    : students;

  const toggleStudent = (id: string) => {
    setForm(prev => ({
      ...prev,
      studentIds: prev.studentIds.includes(id)
        ? prev.studentIds.filter(x => x !== id)
        : [...prev.studentIds, id],
    }));
  };

  const toggleAll = () => {
    setForm(prev => ({
      ...prev,
      studentIds: prev.studentIds.length === filteredStudents.length
        ? []
        : filteredStudents.map(s => s.studentId),
    }));
  };

  const set = (field: string, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMentorId)          { toast.error('Select the mentor whose students you are teaching'); return; }
    if (form.studentIds.length === 0) { toast.error('Select at least one student'); return; }
    const given = form.attendance === 'Absent' ? [] : tasksToSend(tasks);
    if (given.some(t => !t.title)) { toast.error('Give each task a title'); return; }
    setLoading(true);
    const toSubmit = students.filter(s => form.studentIds.includes(s.studentId));
    let done = 0; let failed = 0; let firstError = '';
    for (const s of toSubmit) {
      setProgress(`Logging ${s.name}… (${done + 1}/${toSubmit.length})`);
      try {
        await submitClass({
          studentId:        s.studentId,
          takenByMentorId:  coordinatorMentorId,
          date:             form.date,
          attendance:       form.attendance,
          absentType:       form.attendance === 'Absent' ? form.absentType : undefined,
          sendAbsentEmail:  form.attendance === 'Absent' && form.absentType !== 'Leave',
          portion:          form.portion,
          topic:            form.topic,
          startTime:        form.startTime,
          endTime:          form.endTime,
          // The class's task status follows the tasks typed in it
          completionStatus: 'Task Given',
          tasks:            given,
          remark:           form.remark,
        });
        done++;
      } catch (err: any) {
        failed++;
        firstError = firstError || err.message;
      }
    }
    setLoading(false);
    setProgress('');
    if (done > 0) toast.success(`Class logged for ${done} student${done !== 1 ? 's' : ''}!${given.length ? ` ${given.length} task${given.length === 1 ? '' : 's'} sent to each.` : ''}`);
    if (failed > 0) toast.error(`${failed} submission${failed !== 1 ? 's' : ''} failed${firstError ? `: ${firstError}` : ''}`);
    if (failed > 0 && done === 0) return;
    setForm({ ...EMPTY_FORM, date: todayStr() });
    setTasks([emptyTask()]);
    setStudentSearch('');
  };

  const isAbsent = form.attendance === 'Absent';

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-5">

      {/* Mentor selector */}
      <div className="rounded-xl border-2 border-[#2b2240]/20 bg-[#f8f6ff] p-4 space-y-3">
        <p className="text-xs font-semibold text-[#2b2240] uppercase tracking-wide">Select Mentor &amp; Students</p>

        <Field label="Mentor *">
          <select value={selectedMentorId} onChange={e => handleMentorChange(e.target.value)} required className="input-base">
            <option value="">— select mentor —</option>
            {allMentors.map(m => (
              <option key={m.mentorId} value={m.mentorId}>{m.mentorName}</option>
            ))}
          </select>
        </Field>

        {/* Multi-student checklist */}
        <Field label={`Students * ${form.studentIds.length > 0 ? `(${form.studentIds.length} selected)` : ''}`}>
          {loadingStudents ? (
            <div className="text-sm text-gray-400 py-3">Loading students…</div>
          ) : !selectedMentorId ? (
            <div className="text-sm text-gray-400 py-3 text-center border-2 border-dashed border-gray-200 rounded-xl">
              Select a mentor first
            </div>
          ) : students.length === 0 ? (
            <div className="text-sm text-amber-700 bg-amber-50 rounded-xl px-3 py-3 border border-amber-200">
              No active students under this mentor.
            </div>
          ) : (
            <div className="border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-[#2b2240]">
              {/* Search + select-all bar */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
                <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <input type="text" placeholder="Search students…" value={studentSearch}
                  onChange={e => setStudentSearch(e.target.value)}
                  className="flex-1 text-xs bg-transparent outline-none" />
                <button type="button" onClick={toggleAll}
                  className="text-[10px] font-bold px-2 py-0.5 rounded border border-gray-200 text-gray-500 hover:bg-white whitespace-nowrap flex-shrink-0">
                  {form.studentIds.length === filteredStudents.length && filteredStudents.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              {/* Checklist */}
              <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {filteredStudents.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">No students found</p>
                ) : filteredStudents.map(s => {
                  const checked = form.studentIds.includes(s.studentId);
                  return (
                    <label key={s.studentId}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer select-none transition-colors ${checked ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleStudent(s.studentId)}
                        className="w-4 h-4 accent-purple-600 flex-shrink-0" />
                      <span className="text-sm font-medium flex-1 truncate" style={{ color: checked ? '#5b21b6' : '#374151' }}>{s.name}</span>
                      <span className="text-[10px] text-gray-400 font-mono flex-shrink-0">{s.studentId}</span>
                      {checked && <span className="text-purple-500 text-xs flex-shrink-0">✓</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </Field>
      </div>

      {/* Progress indicator during batch submit */}
      {progress && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 border border-purple-200 text-xs text-purple-700 font-medium">
          <div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          {progress}
        </div>
      )}

      {/* Date */}
      <Field label="Date *">
        <input type="date" value={form.date} onChange={e => set('date', e.target.value)} required className="input-base" />
      </Field>

      {/* Attendance */}
      <Field label="Attendance *">
        <div className="flex gap-3">
          {(['Present', 'Absent'] as const).map(opt => (
            <button key={opt} type="button" onClick={() => set('attendance', opt)}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all"
              style={form.attendance === opt
                ? opt === 'Present'
                  ? { background: '#16a34a', color: '#fff', borderColor: '#16a34a' }
                  : { background: '#dc2626', color: '#fff', borderColor: '#dc2626' }
                : { background: '#f9fafb', color: '#374151', borderColor: '#e5e7eb' }}>
              {opt}
            </button>
          ))}
        </div>
      </Field>

      {/* Absent reason */}
      {isAbsent && (
        <Field label="Absence reason *">
          <div className="flex gap-2 flex-wrap">
            {([
              { value: 'Informed',    label: 'Informed',        activeStyle: { background: '#d97706', color: '#fff', borderColor: '#d97706' } },
              { value: 'NotInformed', label: 'Not Informed',    activeStyle: { background: '#dc2626', color: '#fff', borderColor: '#dc2626' } },
              { value: 'Leave',       label: 'Leave (Institute)', activeStyle: { background: '#2563eb', color: '#fff', borderColor: '#2563eb' } },
            ] as { value: AbsentType; label: string; activeStyle: React.CSSProperties }[]).map(opt => (
              <button key={opt.value} type="button"
                onClick={() => set('absentType', opt.value)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all"
                style={form.absentType === opt.value ? opt.activeStyle : { background: '#f9fafb', color: '#374151', borderColor: '#e5e7eb' }}>
                {opt.label}
              </button>
            ))}
          </div>
          {form.absentType !== 'Leave' && (
            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
              style={{ background: form.absentType === 'Informed' ? '#fffbeb' : '#fef2f2',
                       color:     form.absentType === 'Informed' ? '#92400e' : '#991b1b' }}>
              <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              {form.absentType === 'Informed'
                ? 'A polite acknowledgement email will be sent to the student automatically.'
                : 'A formal warning email will be sent to the student automatically.'}
            </div>
          )}
        </Field>
      )}

      {/* Portion + Topic */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={`Portion Covered${isAbsent ? ' (optional)' : ' *'}`}>
          <input type="text" value={form.portion} onChange={e => set('portion', e.target.value)}
            required={!isAbsent} disabled={isAbsent} placeholder="e.g. Chapter 3 — Loops"
            className="input-base disabled:bg-gray-50 disabled:text-gray-400" />
        </Field>
        <Field label={`Topic / Task${isAbsent ? ' (optional)' : ' *'}`}>
          <input type="text" value={form.topic} onChange={e => set('topic', e.target.value)}
            required={!isAbsent} disabled={isAbsent} placeholder="e.g. Practice problems"
            className="input-base disabled:bg-gray-50 disabled:text-gray-400" />
        </Field>
      </div>

      {/* Times */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Start Time">
          <input type="time" value={form.startTime} onChange={e => set('startTime', e.target.value)}
            disabled={isAbsent} className="input-base disabled:bg-gray-50 disabled:text-gray-400" />
        </Field>
        <Field label="End Time">
          <input type="time" value={form.endTime} onChange={e => set('endTime', e.target.value)}
            disabled={isAbsent} className="input-base disabled:bg-gray-50 disabled:text-gray-400" />
        </Field>
      </div>

      {/* Tasks given (only for present sessions): each goes to the students
          ticked, who send it from My Tasks; their mentor reviews it */}
      {!isAbsent && (
        <Field label="Tasks given in this class">
          <TaskRows rows={tasks} onChange={setTasks} minDate={form.date} />
          <p className="text-[11px] text-gray-500 mt-1.5 px-1">The students send their work from <strong>My Tasks</strong>; their mentor reviews it. No task given? Leave the rows empty.</p>
        </Field>
      )}

      {/* Remark */}
      <Field label="Remark (optional)">
        <textarea value={form.remark} onChange={e => set('remark', e.target.value)}
          rows={2} placeholder="Any notes about this session…" className="input-base resize-none" />
      </Field>

      <button type="submit" disabled={loading}
        className="w-full py-3 rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
        style={{ background: loading ? '#9ca3af' : 'linear-gradient(135deg,#312741,#5a4a6f)', color: '#fff' }}>
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Submitting…
          </span>
        ) : form.studentIds.length > 1
          ? `Submit for ${form.studentIds.length} Students`
          : 'Submit Class Record'}
      </button>
    </form>
  );
};

// ==================== UPDATE STATUS TAB ======================

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  'Completed':     { bg: '#dcfce7', text: '#15803d' },
  'Task Given':    { bg: '#fef9c3', text: '#854d0e' },
  'Not Completed': { bg: '#fee2e2', text: '#b91c1c' },
};

const UpdateStatusTab: React.FC<{ mentorId: string }> = ({ mentorId }) => {
  const [rows, setRows]     = useState<RecentClassRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editing, setEditing] = useState<RecentClassRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRecentClasses(mentorId);
      setRows(res.rows);
    } catch (err: any) {
      toast.error('Failed to load recent classes: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [mentorId]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (row: RecentClassRow, newStatus: CompletionStatus) => {
    if (newStatus === row.completionStatus || saving === row.logRowIndex) return;
    setSaving(row.logRowIndex);
    try {
      await updateClassStatus(mentorId, row.logRowIndex, row.studentId, row.date, newStatus);
      setRows(prev => prev.map(r =>
        r.logRowIndex === row.logRowIndex ? { ...r, completionStatus: newStatus } : r
      ));
      toast.success('Status updated!');
    } catch (err: any) {
      toast.error(err.message || 'Update failed');
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-40 text-gray-400">Loading recent classes…</div>;

  if (rows.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-400 text-sm">
        No class records found in the last 14 days.
      </div>
    );
  }

  const byDate: Record<string, RecentClassRow[]> = {};
  rows.forEach(r => { if (!byDate[r.date]) byDate[r.date] = []; byDate[r.date].push(r); });

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">Classes you logged in the last 14 days. Update status using the buttons.</p>
      {Object.entries(byDate).map(([date, dateRows]) => {
        const isToday = date === todayStr();
        const isYest  = (() => { const y = new Date(); y.setDate(y.getDate() - 1); return date === y.toISOString().slice(0,10); })();
        return (
          <div key={date} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" style={{ background: '#f8f6ff' }}>
              <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#312741' }}>
                {isToday ? 'Today' : isYest ? 'Yesterday' : date}
              </span>
              <span className="text-xs text-gray-400">{date}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {dateRows.map(row => {
                const isSaving = saving === row.logRowIndex;
                const sc = STATUS_COLORS[row.completionStatus] ?? { bg: '#f3f4f6', text: '#374151' };
                return (
                  <div key={row.logRowIndex} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-mono font-semibold" style={{ color: '#312741' }}>{row.studentId}</span>
                        <span className="font-medium text-sm text-gray-800 truncate">{row.studentName}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${row.attendance === 'Present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {row.attendance}
                        </span>
                      </div>
                      {row.topic && <p className="text-xs text-gray-500 truncate">{row.topic}</p>}
                      <EditRequestChip req={row.editRequest} />
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
                      style={{ background: sc.bg, color: sc.text }}>{row.completionStatus}</span>
                    <button onClick={() => setEditing(row)}
                      className="px-2.5 py-1 rounded-lg text-xs font-bold border-2 whitespace-nowrap hover:bg-purple-50"
                      style={{ borderColor: '#c4b5fd', color: '#6d28d9' }}>
                      ✏️ {row.editRequest?.status === 'Pending' ? 'Revise Edit' : 'Edit'}
                    </button>
                    {row.attendance === 'Present' && (
                      <div className="flex gap-1.5 flex-wrap">
                        {COMPLETION_OPTIONS.filter(o => o !== row.completionStatus).map(opt => (
                          <button key={opt} disabled={isSaving || saving !== null} onClick={() => handleStatusChange(row, opt)}
                            className="px-2.5 py-1 rounded-lg text-xs font-semibold border-2 transition-all hover:scale-105 disabled:opacity-50 whitespace-nowrap"
                            style={opt === 'Completed'
                              ? { borderColor: '#16a34a', color: '#16a34a', background: '#f0fdf4' }
                              : opt === 'Not Completed'
                              ? { borderColor: '#dc2626', color: '#dc2626', background: '#fef2f2' }
                              : { borderColor: '#d97706', color: '#d97706', background: '#fffbeb' }}>
                            {isSaving ? '…' : opt === 'Completed' ? 'Mark Completed' : opt === 'Not Completed' ? 'Not Done' : 'Task Given'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <button onClick={load} className="text-xs text-blue-600 hover:underline">Refresh</button>

      {editing && (
        <ClassLogEditModal row={editing} mentorId={mentorId}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
};

// ==================== MAIN PAGE ==============================

type Tab = 'log' | 'update';

const CoordinatorForm: React.FC = () => {
  const { session } = useTracker();

  const [tab, setTab]           = useState<Tab>('log');
  const [allMentors, setAllMentors] = useState<MentorInfo[]>([]);
  const [fetching, setFetching] = useState(true);

  const mentorId   = session?.kind === 'coordinator' ? session.mentorId   : '';
  const mentorName = session?.kind === 'coordinator' ? session.mentorName : '';

  const load = useCallback(async () => {
    setFetching(true);
    try {
      const res = await getMentors();
      setAllMentors(res.mentors);
    } catch (err: any) {
      toast.error('Failed to load mentors: ' + err.message);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!session || session.kind !== 'coordinator') return null;

  return (
    <TrackerLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-5">
          <h1 className="text-2xl font-bold" style={{ color: '#2b2240' }}>Class Tracker</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Log and update class sessions &nbsp;&middot;&nbsp;
            <span className="font-medium text-gray-700">{mentorName}</span>
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#F0B429', color: '#2b2240' }}>
              Coordinator
            </span>
          </p>
        </div>

        <EditLoggedClassLink to="/tracker/coordinator/edit-logs"
          hint="Fix any student's class logs, one or many at once — admin approves the change" />

        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5">
          {([
            { key: 'log',    label: 'Log New Class' },
            { key: 'update', label: 'Update / Edit' },
          ] as { key: Tab; label: string }[]).map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
              style={tab === t.key
                ? { background: '#2b2240', color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.2)' }
                : { color: '#6b7280' }}>
              {t.label}
            </button>
          ))}
        </div>

        {fetching ? (
          <div className="flex items-center justify-center h-40 text-gray-400">Loading…</div>
        ) : tab === 'log' ? (
          <LogClassTab coordinatorMentorId={mentorId} allMentors={allMentors} />
        ) : (
          <UpdateStatusTab mentorId={mentorId} />
        )}
      </div>

      <style>{`
        .input-base { width:100%; padding:0.625rem 0.875rem; border:2px solid #e5e7eb; border-radius:0.5rem; font-size:0.875rem; color:#111827; transition:border-color .15s; outline:none; background:#fff; }
        .input-base:focus { border-color:#2b2240; }
        .input-base:disabled { cursor:not-allowed; }
      `}</style>
    </TrackerLayout>
  );
};

export default CoordinatorForm;
